import { describeError, useApi } from '@classgo/app-kit';
import { createEvaluationInputSchema, type EvaluationView, type GradesView } from '@classgo/shared';
import { Button, Checkbox, EmptyState, Input, Modal, Select, Table } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { refusalOf } from '../../attendance/api';
import { curriculumQueries } from '../../curriculum/api';
import { gradebookApi, gradebookQueries } from '../../gradebook/api';
import type { GradesCell } from '../../gradebook/types';
import { useT } from '../../i18n';
import { formatDate } from '../../lib/dates';
import { useForm } from '../../lib/form';
import { can } from '../../shell';
import { useTenant } from '../../tenant/tenant-provider';
import type { EvaluationDraft } from './types';

const draftOf = (evaluation: EvaluationView | null): EvaluationDraft => ({
  name: evaluation?.name ?? '',
  kind: evaluation?.kind ?? 'activity',
  weight: evaluation ? String(evaluation.weight) : '',
  dueOn: evaluation?.dueOn ?? '',
  topicIds: evaluation?.topics.map((topic) => topic.id) ?? [],
});

/**
 * The form of an evaluation (new or edited): name, kind, relative weight, due date and the
 * topics of the plan of the group's grade in this subject (any period, as the API allows).
 * Validated with the shared schema before it is sent; the API's refusals come back to the
 * fields, or to the top when they are the cell's (published).
 */
const EvaluationForm = ({
  cell,
  gradeId,
  evaluation,
  onClose,
  onSaved,
}: {
  cell: GradesCell;
  gradeId: string;
  evaluation: EvaluationView | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const { session } = useTenant();
  const queries = useQueryClient();
  const plan = useQuery({
    ...curriculumQueries.plan(api, gradeId, cell.subjectId),
    enabled: gradeId !== '' && can(session, 'curriculum:read'),
  });
  // The create schema validates an edit too: its fields are the same, and the cell is known.
  const form = useForm<EvaluationDraft, typeof createEvaluationInputSchema>({
    schema: createEvaluationInputSchema,
    initial: draftOf(evaluation),
    toInput: (values) => ({
      ...cell,
      name: values.name,
      kind: values.kind,
      weight: values.weight.trim() === '' ? undefined : Number(values.weight.replace(',', '.')),
      dueOn: values.dueOn === '' ? null : values.dueOn,
      topicIds: values.topicIds,
    }),
  });
  const submit = async () => {
    const done = await form.submit(async (input) => {
      if (evaluation) {
        const { name, kind, weight, dueOn, topicIds } = input;
        await gradebookApi(api).updateEvaluation(evaluation.id, {
          name,
          kind,
          weight,
          dueOn,
          topicIds,
        });
      } else {
        await gradebookApi(api).createEvaluation(input);
      }
      await queries.invalidateQueries({ queryKey: ['school', 'evaluations'] });
      await queries.invalidateQueries({ queryKey: ['school', 'period-grades'] });
    });
    if (done) onSaved();
  };
  const toggle = (topicId: string, checked: boolean) =>
    form.set(
      'topicIds',
      checked
        ? [...form.values.topicIds, topicId]
        : form.values.topicIds.filter((id) => id !== topicId),
    );
  const periods = plan.data?.periods.filter((period) => period.topics.length > 0) ?? [];
  return (
    <Modal
      open
      onClose={onClose}
      title={evaluation ? t('gradebook.evaluations.edit') : t('gradebook.evaluations.new')}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={form.busy} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {form.errors.form ? (
          <p
            role="alert"
            className="rounded-control bg-danger-100 px-3 py-2 text-sm text-danger-700"
          >
            {form.errors.form}
          </p>
        ) : null}
        <Input
          label={t('gradebook.evaluations.field.name')}
          value={form.values.name}
          onChange={(event) => form.set('name', event.target.value)}
          error={form.fieldError('name')}
          maxLength={120}
          autoFocus
        />
        <div className="grid gap-3 sm:grid-cols-3">
          <Select
            label={t('gradebook.evaluations.field.kind')}
            value={form.values.kind}
            onChange={(event) =>
              form.set('kind', event.target.value === 'exam' ? 'exam' : 'activity')
            }
            options={[
              { value: 'activity', label: t('gradebook.evaluations.kind.activity') },
              { value: 'exam', label: t('gradebook.evaluations.kind.exam') },
            ]}
          />
          <Input
            label={t('gradebook.evaluations.field.weight')}
            inputMode="decimal"
            value={form.values.weight}
            onChange={(event) => form.set('weight', event.target.value)}
            error={form.fieldError('weight')}
            help={t('gradebook.evaluations.field.weightHelp')}
          />
          <Input
            label={t('gradebook.evaluations.field.dueOn')}
            type="date"
            value={form.values.dueOn}
            onChange={(event) => form.set('dueOn', event.target.value)}
            error={form.fieldError('dueOn')}
          />
        </div>
        {plan.data ? (
          <fieldset>
            <legend className="text-sm font-medium text-gray-900">
              {t('gradebook.evaluations.field.topics')}
            </legend>
            {periods.length === 0 ? (
              <p className="mt-1 text-sm text-gray-600">{t('gradebook.evaluations.topics.none')}</p>
            ) : (
              <div className="mt-2 space-y-3">
                {periods.map((period) => (
                  <div key={period.periodPosition} className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t('gradebook.evaluations.topics.period', {
                        position: period.periodPosition,
                      })}
                    </p>
                    {period.topics.map((topic) => (
                      <Checkbox
                        key={topic.id}
                        label={topic.title}
                        checked={form.values.topicIds.includes(topic.id)}
                        onChange={(event) => toggle(topic.id, event.target.checked)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </fieldset>
        ) : null}
      </form>
    </Modal>
  );
};

/**
 * The "Evaluaciones" tab: the evaluations of the cell with kind, weight, date, topics and how
 * many students have a score; "Nueva evaluación", and per row "Editar" and "Eliminar" (behind a
 * confirmation that says how many scores go with it). Nothing to write while the subject is
 * published.
 */
export const EvaluationsTab = ({
  cell,
  view,
  gradeId,
}: {
  cell: GradesCell;
  view: GradesView;
  gradeId: string;
}) => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const evaluations = useQuery(gradebookQueries.evaluations(api, cell));
  const [editing, setEditing] = useState<EvaluationView | null | 'new'>(null);
  const [deleting, setDeleting] = useState<EvaluationView | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => gradebookApi(api).deleteEvaluation(id),
    onSuccess: async () => {
      setDeleting(null);
      setNotice(t('gradebook.evaluations.deleted'));
      await queries.invalidateQueries({ queryKey: ['school', 'evaluations'] });
      await queries.invalidateQueries({ queryKey: ['school', 'period-grades'] });
    },
  });
  const items = evaluations.data?.items ?? [];
  const editable = !view.published;
  const total = view.rows.length;
  const weightSum = items.reduce((sum, item) => sum + item.weight, 0);
  if (evaluations.isError) {
    return (
      <EmptyState
        title={t('entity.error.title')}
        description={describeError(t, evaluations.error)}
      />
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600">
          {editable
            ? t('gradebook.evaluations.weightSum', { sum: weightSum })
            : t('gradebook.state.locked')}
        </p>
        {editable ? (
          <Button
            onClick={() => {
              setNotice(null);
              setEditing('new');
            }}
          >
            {t('gradebook.evaluations.new')}
          </Button>
        ) : null}
      </div>
      {notice ? (
        <p
          role="status"
          className="rounded-control bg-success-100 px-3 py-2 text-sm text-success-700"
        >
          {notice}
        </p>
      ) : null}
      <Table<EvaluationView>
        caption={t('gradebook.evaluations.caption')}
        rows={items}
        rowKey={(row) => row.id}
        loading={evaluations.isPending}
        empty={<EmptyState title={t('gradebook.evaluations.empty')} />}
        columns={[
          {
            key: 'name',
            header: t('gradebook.evaluations.column.name'),
            cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>,
          },
          {
            key: 'kind',
            header: t('gradebook.evaluations.column.kind'),
            cell: (row) => t(`gradebook.evaluations.kind.${row.kind}`),
          },
          {
            key: 'weight',
            header: t('gradebook.evaluations.column.weight'),
            align: 'right',
            cell: (row) => row.weight,
          },
          {
            key: 'dueOn',
            header: t('gradebook.evaluations.column.dueOn'),
            cell: (row) => (row.dueOn ? formatDate(row.dueOn) : t('common.none')),
          },
          {
            key: 'topics',
            header: t('gradebook.evaluations.column.topics'),
            cell: (row) =>
              row.topics.length === 0
                ? t('common.none')
                : row.topics.map((topic) => topic.title).join(' · '),
          },
          {
            key: 'scored',
            header: t('gradebook.evaluations.column.scored'),
            align: 'right',
            cell: (row) => t('gradebook.evaluations.scored', { scored: row.scoredCount, total }),
          },
          ...(editable
            ? [
                {
                  key: 'actions',
                  header: t('gradebook.column.actions'),
                  align: 'right' as const,
                  cell: (row: EvaluationView) => (
                    <span className="inline-flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`${t('gradebook.evaluations.editAction')}: ${row.name}`}
                        onClick={() => {
                          setNotice(null);
                          setEditing(row);
                        }}
                      >
                        {t('gradebook.evaluations.editAction')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`${t('gradebook.evaluations.delete')}: ${row.name}`}
                        onClick={() => {
                          setNotice(null);
                          remove.reset();
                          setDeleting(row);
                        }}
                      >
                        {t('gradebook.evaluations.delete')}
                      </Button>
                    </span>
                  ),
                },
              ]
            : []),
        ]}
      />
      {editing !== null ? (
        <EvaluationForm
          cell={cell}
          gradeId={gradeId}
          evaluation={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setNotice(
              editing === 'new'
                ? t('gradebook.evaluations.created')
                : t('gradebook.evaluations.updated'),
            );
            setEditing(null);
          }}
        />
      ) : null}
      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title={t('gradebook.evaluations.delete.title', { name: deleting?.name ?? '' })}
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => {
                if (deleting) remove.mutate(deleting.id);
              }}
            >
              {t('gradebook.evaluations.delete')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            {t('gradebook.evaluations.delete.body', { scored: deleting?.scoredCount ?? 0 })}
          </p>
          {remove.isError ? (
            <p
              role="alert"
              className="rounded-control bg-danger-100 px-3 py-2 text-sm text-danger-700"
            >
              {t('gradebook.evaluations.deleteFailed')}{' '}
              {refusalOf(remove.error) ?? describeError(t, remove.error)}
            </p>
          ) : null}
        </div>
      </Modal>
    </div>
  );
};
